import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { PlusIcon, ListTreeIcon } from 'lucide-react'

import { INTEGRATION_STATUSES } from 'constants/statuses'
import {
  INTEGRATIONS,
  INTEGRATION_CATEGORY_LABELS,
  usedIntegrationCategories,
  type IntegrationCategory,
} from 'constants/integrations'

import { useReferrerState } from 'hooks'
import { useShopifyConnectionStatus, useIntegrationRequests } from 'hooks/api'
import { ROUTES } from 'routes'

import { alpha, useTheme, Stack, Typography, Grid, Fade } from '@mui/material'

import { Button, Card } from '@repo/ui'
import { Search, StatusChip, IntegrationLogo } from 'components'
import { VoteButton } from './components/VoteButton'
import { PriorityIndicator } from './components/PriorityIndicator'
import { SuggestIntegrationModal } from './components/SuggestIntegrationModal'

export const IntegrationsPage = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  // Referrer state handed to the integration drill-in so its Header offers
  // "← Back to Integrations".
  const referrer = useReferrerState()

  const { data: shopifyStatus } = useShopifyConnectionStatus()
  const { data: requests = [] } = useIntegrationRequests()

  const isIntegrationConnected = (key: string) => (key === 'shopify' ? shopifyStatus?.connected === true : false)

  const [search, setSearch] = useState('')
  const [suggestOpen, setSuggestOpen] = useState(false)
  // Chips are derived from what the page renders — the available integrations plus the
  // vote items — so a chip can never come up empty. Deliberately NOT narrowed by the
  // active search/category filters: chips that vanish as you filter make the filter bar
  // move under the cursor and can strand a selection with no chip left to clear it.
  const categories = usedIntegrationCategories(
    requests.flatMap((r) => (r.categories?.length ? r.categories : [r.category])),
  )
  const [selectedCategories, setSelectedCategories] = useState<IntegrationCategory[]>([])

  const handleCategorySelect = (category: IntegrationCategory) => {
    setSelectedCategories(
      selectedCategories.includes(category)
        ? selectedCategories.filter((i) => i !== category)
        : [...selectedCategories, category],
    )
  }

  const chipSx = (active: boolean) => ({
    py: 2,
    px: 3,
    mr: 2,
    mb: 2,
    cursor: 'pointer',
    borderRadius: 2,
    border: `1px solid ${theme.palette.grey[300]}`,
    backgroundColor: active ? alpha(theme.palette.brand[500], 0.1) : undefined,
    borderColor: active ? theme.palette.brand[500] : undefined,
    color: active ? theme.palette.brand[500] : undefined,
    '&:hover': {
      backgroundColor: alpha(theme.palette.brand[500], 0.1),
      borderColor: theme.palette.brand[500],
      color: theme.palette.brand[800],
    },
  })

  const matchesFilters = (name: string, cats: string[]) =>
    (selectedCategories.length === 0 || selectedCategories.some((cat) => cats.includes(cat))) &&
    name.toLowerCase().includes(search.toLowerCase())

  // Available (connectable) integrations come from the registry; the vote list
  // ("what's next") comes from the backend so promoted suggestions show up.
  const availableIntegrations = INTEGRATIONS.filter(
    (i) => i.availability === 'available' && matchesFilters(i.name, i.categories),
  )
  const voteItems = requests.filter((r) => matchesFilters(r.name, r.categories?.length ? r.categories : [r.category]))

  return (
    <Stack>
      <Stack direction="row" alignItems="center" justifyContent="space-between" pb={4}>
        <Search
          defaultValue={search}
          sx={{ maxWidth: '300px' }}
          onChange={(value) => setSearch(value)}
          onClear={() => setSearch('')}
        />
        {/* TOOLBAR_CONTROL_HEIGHT (49) via `size="large"` — FIELD PARITY, not importance.
            §29 decides this per BAND, not per element type: a control sharing its row with a
            49px field matches the field; the page header is 40 precisely because it has no
            field beside it.

            This read 40 (the `medium` default) and was defended on the grounds that "the
            search sits at the far end of the row where the eye cannot compare the two". Both
            halves were wrong, measured live at 1280x800: button 40.0, field 50.0, tops offset
            by 5px, and the two only 279px apart in the same visual band — the mismatch was
            spotted unprompted. That is the same defect §29 records for the table toolbar,
            where 40px buttons sat 9px short of the search bar immediately to their left.

            The Filter/Sort/Views controls this used to cite are themselves 49
            (TOOLBAR_CONTROL_HEIGHT), so matching them and matching the field are the same
            instruction, not competing ones. */}
        <Button variant="secondary" size="large" disableElevation startIcon={<PlusIcon size={16} strokeWidth={1} />} onClick={() => setSuggestOpen(true)}>
          Suggest an integration
        </Button>
      </Stack>

      <Stack spacing={4} pb={4}>
        <Grid container>
          <Grid item key={'all'} onClick={() => setSelectedCategories([])} sx={chipSx(selectedCategories.length === 0)}>
            All
          </Grid>
          {categories.map((category) => (
            <Grid item key={category} onClick={() => handleCategorySelect(category)} sx={chipSx(selectedCategories.includes(category))}>
              {INTEGRATION_CATEGORY_LABELS[category]}
            </Grid>
          ))}
        </Grid>
      </Stack>

      {/* Available integrations (registry) */}
      <Grid container spacing={3}>
        {availableIntegrations.map((integration, index) => {
          const connected = isIntegrationConnected(integration.key)
          return (
            <Fade in appear timeout={400} style={{ transitionDelay: `${Math.min(index, 8) * 45}ms` }} key={integration.key}>
              <Grid item xs={12} sm={6} md={4}>
                <Link to={ROUTES.SETTINGS_COMPANY_INTEGRATION(integration.key)} state={referrer}>
                  <Card sx={{ p: 4 }}>
                    <Stack spacing={2}>
                      <Stack spacing={3} direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ overflowX: 'hidden' }}>
                        <IntegrationLogo src={integration.logo} alt={integration.name} />
                        {connected && <StatusChip statuses={INTEGRATION_STATUSES} status="connected" />}
                      </Stack>
                      <Typography variant="h6" sx={{ pt: 2 }}>{integration.name}</Typography>
                      <Typography variant="body4" color="text.secondary">{integration.summary}</Typography>
                      {connected && (
                        <Stack direction="row" spacing={1.5} sx={{ pt: 1 }}>
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={0.75}
                            onClick={(e) => {
                              e.preventDefault()
                              navigate(
                                `${ROUTES.SETTINGS_COMPANY_INTEGRATION(integration.key)}?tab=field-mapping`,
                                { state: referrer },
                              )
                            }}
                            sx={{
                              cursor: 'pointer',
                              color: 'text.secondary',
                              '&:hover': { color: 'text.primary' },
                            }}
                          >
                            <ListTreeIcon size={13} strokeWidth={1.5} />
                            <Typography variant="body4">Field mapping</Typography>
                          </Stack>
                        </Stack>
                      )}
                    </Stack>
                  </Card>
                </Link>
              </Grid>
            </Fade>
          )
        })}
      </Grid>

      {/* Vote on what's next (backend requests) */}
      {voteItems.length > 0 && (
        <>
          <Typography variant="h6" sx={{ pt: 6, pb: 1 }}>Vote on what's next</Typography>
          <Typography variant="body3" color="text.secondary" sx={{ pb: 4, lineHeight: 1.5 }}>
            Upvote the integrations you want most. The most-wanted move up our roadmap.
          </Typography>
          <Grid container spacing={3}>
            {voteItems.map((req, index) => (
              <Fade in appear timeout={400} style={{ transitionDelay: `${Math.min(index, 8) * 45}ms` }} key={req.id}>
                <Grid item xs={12} sm={6} md={4}>
                  <Card sx={{ p: 4, minHeight: 248, display: 'flex', flexDirection: 'column' }}>
                    <Stack spacing={2} sx={{ flexGrow: 1 }}>
                      <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
                        <IntegrationLogo
                          src={req.logo}
                          alt={req.name}
                          fallback={
                            <Typography variant="h6" color="text.secondary">{req.name.charAt(0).toUpperCase()}</Typography>
                          }
                        />
                        {req.status === 'shipping_soon' && (
                          <Typography variant="body4" sx={{ py: 1, px: 2, borderRadius: 2, whiteSpace: 'nowrap', color: theme.palette.brand[700], backgroundColor: theme.palette.brand[100], border: `1px solid ${theme.palette.brand[200]}` }}>
                            Building
                          </Typography>
                        )}
                      </Stack>
                      <Typography variant="h6" sx={{ pt: 2 }}>{req.name}</Typography>
                      {req.description && (
                        <Typography variant="body4" color="text.secondary">{req.description}</Typography>
                      )}
                      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ pt: 2, mt: 'auto' }}>
                        <Stack spacing={2.5}>
                          <Typography variant="body4" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: 10, lineHeight: 1 }}>
                            Popularity
                          </Typography>
                          <PriorityIndicator tier={req.priorityTier} />
                        </Stack>
                        <VoteButton requestId={req.id} voted={req.hasVoted} integrationName={req.name} />
                      </Stack>
                    </Stack>
                  </Card>
                </Grid>
              </Fade>
            ))}

            {/* Contextual "didn't find it?" suggest entry — catches the user right
                after they've scanned the list. Complements the top-level button. */}
            <Fade in appear timeout={400} style={{ transitionDelay: `${Math.min(voteItems.length, 8) * 45}ms` }}>
              <Grid item xs={12} sm={6} md={4}>
                <Card
                  onClick={() => setSuggestOpen(true)}
                  sx={{
                    p: 4,
                    height: '100%',
                    minHeight: 248,
                    cursor: 'pointer',
                    boxShadow: 'none',
                    border: `1px dashed ${theme.palette.divider}`,
                    backgroundColor: 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.15s ease-in',
                    '&:hover': {
                      borderColor: theme.palette.brand[500],
                      backgroundColor: alpha(theme.palette.brand[500], 0.04),
                    },
                  }}
                >
                  <Stack spacing={1.5} alignItems="center" textAlign="center">
                    <Stack
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: alpha(theme.palette.brand[500], 0.1),
                        color: theme.palette.brand[600],
                      }}
                    >
                      <PlusIcon size={20} strokeWidth={1.5} />
                    </Stack>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Don't see an integration you use?
                    </Typography>
                    <Typography variant="body4" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                      Suggest one and we'll add it to the list.
                    </Typography>
                  </Stack>
                </Card>
              </Grid>
            </Fade>
          </Grid>
        </>
      )}

      <SuggestIntegrationModal open={suggestOpen} onClose={() => setSuggestOpen(false)} />
    </Stack>
  )
}
